import { useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useParams } from "react-router";
import { Badge, EmptyState, Icon, isIconName, type IconName } from "@agentsaas/ui";
import type { Capability, CapabilityFile } from "@agentsaas/protocol";
import { useT } from "../app/i18n";
import { formatBytes } from "../app/format";
import { SubFrame, TenantCrumb } from "../layout/SubFrame";
import { CodeEditor } from "../files/CodeEditor";
import { buildTree, fileSize, flattenTree, isText, type TreeNode } from "./model";

/** 能力包 manifest 里的 icon 是任意字符串；不认识的退回 sparkles */
export function CapIcon({ icon, size = 20 }: { icon: string; size?: number }) {
  const name: IconName = isIconName(icon) ? icon : "sparkles";
  return (
    <span className="cap-icon" aria-hidden="true">
      <Icon name={name} size={size} />
    </span>
  );
}

export type CapSection = "library" | "secrets" | "mcp" | "media";

/** 工作区级"能力与集成"框架：能力库 / 密钥 / MCP 服务 / 媒体供应商 */
export function CapFrame({ section, children }: { section: CapSection; children: ReactNode }) {
  const { tid = "" } = useParams();
  const { t } = useT();
  const items = [
    { id: "library", to: `/t/${tid}/capabilities`, label: t("cap.nav.library"), icon: "sparkles" as const },
    { id: "secrets", to: `/t/${tid}/integrations/secrets`, label: t("cap.nav.secrets"), icon: "key" as const },
    { id: "mcp", to: `/t/${tid}/integrations/mcp`, label: t("cap.nav.mcp"), icon: "plug" as const },
    { id: "media", to: `/t/${tid}/integrations/media`, label: t("cap.nav.media"), icon: "image" as const },
  ];
  return (
    <>
      <TenantCrumb tid={tid} section={t("cap.title")} />
      <SubFrame label={t("cap.title")} items={items} active={section}>
        {children}
      </SubFrame>
    </>
  );
}

export function CapBadges({ cap }: { cap: Pick<Capability, "tenantId" | "status" | "version"> }) {
  const { t } = useT();
  return (
    <>
      <Badge tone={cap.tenantId ? "accent" : "neutral"}>{cap.tenantId ? t("cap.custom") : t("cap.builtin")}</Badge>
      <Badge>{t("cap.version", { version: cap.version })}</Badge>
      {cap.status === "deprecated" ? <Badge tone="warning">{t("cap.deprecated")}</Badge> : null}
    </>
  );
}

/** 分类的显示名：已知分类翻译，其他原样显示 */
export function useCategoryLabel() {
  const { t } = useT();
  return (c: string) => {
    const k = `cap.category.${c}`;
    const s = t(k);
    return s === k ? c : s;
  };
}

// ───────────── 文件树（role=tree，键盘：↑↓ 移动、← → 折叠 / 展开、Enter 打开） ─────────────

export function SkillFileTree({
  files,
  selected,
  onSelect,
  label,
  renderExtra,
}: {
  files: Pick<CapabilityFile, "path" | "executable">[];
  selected: string | null;
  onSelect(path: string): void;
  label: string;
  renderExtra?(path: string): ReactNode;
}) {
  const tree = useMemo(() => buildTree(files), [files]);
  const dirs = useMemo(() => {
    const all = new Set<string>();
    const walk = (ns: TreeNode[]) => ns.forEach((n) => n.type === "dir" && (all.add(n.path), walk(n.children)));
    walk(tree);
    return all;
  }, [tree]);
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const open = new Set([...dirs].filter((d) => !closed.has(d)));
  const rows = flattenTree(tree, open);
  const [focus, setFocus] = useState<string | null>(null);
  const refs = useRef(new Map<string, HTMLElement>());
  const current = focus && rows.some((r) => r.node.path === focus) ? focus : (selected ?? rows[0]?.node.path ?? null);
  const toggle = (p: string, want?: boolean) =>
    setClosed((s) => {
      const n = new Set(s);
      const isOpen = !n.has(p);
      if ((want ?? !isOpen) === true) n.delete(p);
      else n.add(p);
      return n;
    });
  const move = (p: string) => {
    setFocus(p);
    refs.current.get(p)?.focus();
  };
  const onKey = (e: KeyboardEvent, node: TreeNode, i: number) => {
    if (e.key === "ArrowDown" && rows[i + 1]) move(rows[i + 1]!.node.path);
    else if (e.key === "ArrowUp" && rows[i - 1]) move(rows[i - 1]!.node.path);
    else if (e.key === "ArrowRight" && node.type === "dir") toggle(node.path, true);
    else if (e.key === "ArrowLeft" && node.type === "dir" && open.has(node.path)) toggle(node.path, false);
    else if (e.key === "ArrowLeft") {
      const parent = node.path.split("/").slice(0, -1).join("/");
      if (parent) move(parent);
    } else if (e.key === "Enter" || e.key === " ") {
      if (node.type === "dir") toggle(node.path);
      else onSelect(node.path);
    } else return;
    e.preventDefault();
  };
  return (
    <ul className="cap-tree" role="tree" aria-label={label}>
      {rows.map(({ node, depth }, i) => {
        const isDir = node.type === "dir";
        const exec = !isDir && files.find((f) => f.path === node.path)?.executable;
        return (
          <li
            key={`${node.type}:${node.path}`}
            role="treeitem"
            aria-level={depth + 1}
            aria-expanded={isDir ? open.has(node.path) : undefined}
            aria-selected={!isDir ? node.path === selected : undefined}
            tabIndex={node.path === current ? 0 : -1}
            ref={(el) => {
              if (el) refs.current.set(node.path, el);
              else refs.current.delete(node.path);
            }}
            className={`cap-tree__row${node.path === selected ? " is-active" : ""}`}
            style={{ paddingLeft: `calc(${depth} * 16px + 8px)` }}
            onClick={() => (isDir ? toggle(node.path) : onSelect(node.path))}
            onKeyDown={(e) => onKey(e, node, i)}
            onFocus={() => setFocus(node.path)}
            data-testid="cap-tree-item"
            data-path={node.path}
          >
            <Icon name={isDir ? (open.has(node.path) ? "chevronDown" : "chevronRight") : exec ? "terminal" : "file"} size={16} />
            <span className="ellipsis grow">{node.name}</span>
            {!isDir && renderExtra ? renderExtra(node.path) : null}
          </li>
        );
      })}
    </ul>
  );
}

/** 只读文件查看：文本用编辑器（代码高亮），二进制显示大小 */
export function FileViewer({ file, pathKey }: { file: CapabilityFile | undefined; pathKey: string }) {
  const { t, locale } = useT();
  if (!file) return <EmptyState icon="file" title={t("cap.files.pick")} />;
  return (
    <div className="cap-viewer" data-testid="cap-viewer" data-path={file.path}>
      <div className="cap-viewer__head row">
        <code className="grow ellipsis">{file.path}</code>
        {file.executable ? <Badge tone="accent">{t("cap.files.executable")}</Badge> : null}
        <span className="muted small">{formatBytes(fileSize(file), locale)}</span>
      </div>
      <div className="cap-viewer__body">
        {isText(file) ? (
          <CodeEditor path={`capability/${pathKey}/${file.path}`} value={file.content} readOnly />
        ) : (
          <EmptyState icon="file" title={t("cap.files.binary")} />
        )}
      </div>
    </div>
  );
}
